/**
 * Inventory panel (M3.3a C2 + C3) — shows the local player's inventory
 * with weight bar, PropStack entries with quality badges, per-stack
 * tooltips, and the equipment slot grid below.
 *
 * Positioned at top-right of the world canvas, matching the inspection
 * panel's positioning convention (which sits top-left).
 */

import type { PropStackWire } from '../wire';
import { useInventory } from './store';
import { EquipmentPanel } from './EquipmentPanel';
import { StackItem } from './StackItem';
import { WeightBar } from './WeightBar';
import styles from './inventory.module.css';

export function InventoryPanel(): JSX.Element | null {
  const inventory = useInventory((s) => s.inventory);

  if (!inventory) return null;

  const stacks = inventory.stacks as unknown as PropStackWire[];
  const maxWeight = inventory.max_carry_weight;
  // The server doesn't yet send current_weight on the wire; it will
  // arrive once Frank adds it to the snapshot/event path. Until then,
  // show 0 as a placeholder. The weight bar structure is in place so
  // it renders correctly the moment the data flows.
  const currentWeight = 0;

  return (
    <div
      className={styles.panel}
      role="region"
      aria-label="Inventory"
    >
      <header className={styles.header}>
        <h3 className={styles.title}>Inventory</h3>
      </header>
      <div className={styles.body}>
        <WeightBar
          current={currentWeight}
          max={maxWeight}
          label="Carry"
        />
        {stacks.length === 0 ? (
          <div className={styles.emptyState}>Inventory is empty</div>
        ) : (
          <ul className={styles.stackList}>
            {stacks.map((stack, i) => (
              <StackItem key={`${stack.prop_id}-${stack.quality}-${i}`} stack={stack} />
            ))}
          </ul>
        )}
        <EquipmentPanel />
      </div>
    </div>
  );
}
